import get from "lodash/get"
import trim from "lodash/trim"
import { Component } from "react"
// eslint-disable-next-line react-19-upgrade/no-prop-types
import PropTypes from "prop-types"
import { ReactSVG } from "react-svg"
import InputAdornment from "component-lib/Base/components/InputAdornment"
import Button from "component-lib/Base/components/BaseButton"
import isIOS from "utils-lib/isIOS"
import getContentUrl from "utils-lib/getContentUrl"
import getLanguageRoute from "utils-lib/getLanguageRoute"
import { withLDConsumer } from "launchdarkly-react-client-sdk"
import { loginStatusCode } from "utils-lib/userAccount/login"
import { validateLoginEmail, isInternalEmail } from "utils-lib/validation"
import { withLocalization } from "hoc-lib/localization"
import "./Login.scss"
import { isTeamsitePage } from "utils-lib/isTeamsitePage"
import { isMyWMApp } from "utils-lib/isMyWMApp"
import TextInputForm from "component-lib/Base/components/TextInputForm/TextInputForm"
import { getWindow } from "utils-lib/getWindow"
import { updateLoggedInFailure } from "utils-lib/analytics/adobeDataLayer"
import { Link, ButtonArrow, ButtonPrimary, ButtonSecondary } from "../Base"

const ssrWindow = getWindow()

export const strings = {
  en_CA: {
    CONTINUE_AS_GUEST: `Continue as Guest`,
    CREATE_YOUR_ONLINE_PROFILE: `Create Online Profile`,
    FORGOT_PASSWORD: `Forgot email / password?`,
    EMAIL: `Email`,
    ERROR_CREDENTIALS: `The login email and/or password is incorrect.`,
    ERROR_INVALID_EMAIL: `Please enter a valid email.`,
    ERROR_INTERNAL_EMAIL: `Internal users are unable to log in at this time`,
    ERROR_LOCKOUT: `You have attempted to log in unsuccessfully too many times. Please reset your password.`,
    ERROR_REQUIRED: `Required`,
    ERROR_UNREGISTERED: `This email is not registered or your updated login is not confirmed. Please check your email inbox for an email confirmation.`,
    ERROR_UNCONFIRMED_1: `We recognize your account, but your email address is not verified. Please use the link sent to `,
    ERROR_UNCONFIRMED_2: ` to verify your email address.`,
    PASSWORD_EXPIRED_1:
      "As part of our efforts to keep your account secure, you must",
    PASSWORD_EXPIRED_2: "reset your password",
    PASSWORD_EXPIRED_3: "to access your account.",
    LOGIN: `Log in`,
    PASSWORD: `Password`,
    SHOW: `show`,
    HIDE: `hide`,
    TECHNICAL_ERROR: `We apologize for temporary technical issues. Please retry in a few minutes.`,
    OOPS: `Oops! Something went wrong.`,
    DONT_HAVE_AN_ACCOUNT: `Don't have an account?`,
    SIGN_UP: `Sign up`,
  },
  fr_CA: {
    CONTINUE_AS_GUEST: `Continuer comme invité`,
    CREATE_YOUR_ONLINE_PROFILE: `Créez profil en ligne`,
    FORGOT_PASSWORD: `Courriel / mot de passe oublié?`,
    EMAIL: `Courriel`,
    ERROR_CREDENTIALS: `L'adresse courriel de connexion et/ou le mot de passe est incorrect.`,
    ERROR_INVALID_EMAIL: `Veuillez entrer une adresse courriel valide.`,
    ERROR_INTERNAL_EMAIL: `Internal users are unable to log in at this time`,
    ERROR_LOCKOUT: `Vous avez tenté de vous connecter sans succès trop de fois. Veuillez réinitialiser votre mot de passe.`,
    ERROR_REQUIRED: `Requis`,
    ERROR_UNREGISTERED: `Cette adresse courriel n'est pas enregistrée ou la mise à jour de vos coordonnées de connexion n'est pas confirmée. Veuillez vérifier votre boîte de réception pour une confirmation par courriel.`,
    ERROR_UNCONFIRMED_1: `Nous reconnaissons votre compte, mais votre adresse courriel n'est pas vérifiée. Veuillez utiliser le lien envoyé à `,
    ERROR_UNCONFIRMED_2: ` pour vérifier votre adresse courriel.`,
    PASSWORD_EXPIRED_1:
      "Dans le cadre de nos efforts pour assurer la sécurité de votre compte, vous devez",
    PASSWORD_EXPIRED_2: "réinitialiser votre mot de passe",
    PASSWORD_EXPIRED_3: "pour accéder à votre compte.",
    LOGIN: `Connexion`,
    PASSWORD: `Mot de Passe`,
    SHOW: `afficher`,
    HIDE: `cacher`,
    TECHNICAL_ERROR: `Nous nous excusons pour les problèmes techniques temporaires. S'il vous plaît réessayer dans quelques minutes.`,
    OOPS: `Oops! Something went wrong.`, // translate: true
    DONT_HAVE_AN_ACCOUNT: `Avez-vous un compte ? `,
    SIGN_UP: `S'inscrire`,
  },
}

class Login extends Component {
  constructor(props) {
    super(props)
    this.state = {
      errors: {
        credentials: ``,
      },
      showPassword: false,
      isPostSubmit: false,
      isSigningIn: false,
      loginEmail: ``,
      loginPassword: ``,
      fieldValidations: {},
      formStatus: {
        isComplete: false,
        isValid: false,
      },
      blurred: {
        loginEmail: false,
        loginPassword: false,
      },
    }

    /* Check if there is a cookied login and set this.state.loginEmail to that. */
    const userLogin = this.getCookiedLogin()
    if (userLogin !== ``) {
      this.state.loginEmail = userLogin
    }
  }

  componentDidMount() {
    // Polling to populate this.state.loginEmail and this.state.loginPassword for iOS autofill
    if (isIOS()) {
      this.pollingInterval = setInterval(() => {
        const emailInput = ssrWindow.document.querySelector(`input[type=email]`)
        const passwordInput =
          ssrWindow.document.querySelector(`input[type=password]`)

        if (
          emailInput &&
          validateLoginEmail(emailInput.value) &&
          passwordInput &&
          passwordInput.value
        ) {
          this.setState({
            loginEmail: emailInput.value,
            loginPassword: passwordInput.value,
          })
        }
      }, 300)
    }
  }

  componentWillUnmount() {
    if (isIOS()) {
      clearInterval(this.pollingInterval)
    }
  }

  getCookiedLogin() {
    return get(this.props.userAccount, `userDetails.cookiedLogin`, ``)
  }

  handleShowHideClick = () => {
    const { showPassword } = this.state
    this.setState({
      showPassword: !showPassword,
    })
  }

  handleLoginButtonClick = () => {
    const { authenticateUser, redirectUrl, strings, supportLogin } = this.props

    // Prevent submission for empty inputs or invalid email
    if (
      !validateLoginEmail(this.state.loginEmail) ||
      !this.state.loginPassword
    ) {
      this.setValidationError(`credentials`, strings.ERROR_CREDENTIALS)
      updateLoggedInFailure()
      return
    }

    // Prevent submission for internal email addresses
    if (isInternalEmail(this.state.loginEmail)) {
      this.setValidationError(`credentials`, strings.ERROR_INTERNAL_EMAIL)
      updateLoggedInFailure()
      return
    }

    this.setState({
      isPostSubmit: true,
      isSigningIn: true,
    })

    authenticateUser({
      username: trim(this.state.loginEmail),
      password: this.state.loginPassword,
      disablePostLoginRedirect: this.props.disablePostLoginRedirect,
      redirectUrl,
      supportLogin,
      callback: (status) => {
        this.validateCredentials(status)
        this.setState({
          isSigningIn: false,
        })
      },
    })
  }

  onChange =
    (field, cb = () => {}) =>
    (e) => {
      const {
        target: { value },
      } = e
      cb(e)
      this.setState({
        [field]: value,
        isAutofilled: false,
      })
    }

  /* Create a new function using the supplied field name. */
  onValidate = (fieldName) => (isValid) => {
    this.setState({
      fieldValidations: {
        // eslint-disable-next-line react/no-access-state-in-setstate
        ...this.state.fieldValidations,
        [fieldName]: isValid,
      },
    })
  }

  onBlur = (fieldName) => () => {
    this.setState({
      blurred: {
        // eslint-disable-next-line react/no-access-state-in-setstate
        ...this.state.blurred,
        [fieldName]: true,
      },
    })
  }

  setValidationError(field, errorMessage) {
    const { errors } = this.state

    errors[field] = errorMessage
    this.setState({ errors })
  }

  errorFor(field) {
    return this.state.blurred[field] && this.state.errors[field]
  }

  validateCredentials(status) {
    const { strings } = this.props

    if (loginStatusCode.ACCOUNT_NOT_FOUND === status) {
      this.setValidationError(`credentials`, strings.ERROR_UNREGISTERED)
    } else if (loginStatusCode.UNCONFIRMED_EMAIL === status) {
      this.setValidationError(
        `credentials`,
        strings.ERROR_UNCONFIRMED_1 +
          this.state.loginEmail +
          strings.ERROR_UNCONFIRMED_2,
      )
    } else if (loginStatusCode.LOCKED_OUT === status) {
      this.setValidationError(`credentials`, strings.ERROR_LOCKOUT)
    } else if (loginStatusCode.BAD_CREDENTIALS === status) {
      this.setValidationError(`credentials`, strings.ERROR_CREDENTIALS)
    } else if (loginStatusCode.PASSWORD_EXPIRED === status) {
      this.setValidationError(`credentials`, strings.PASSWORD_EXPIRED_1)
    } else if (loginStatusCode.SUCCESS !== status) {
      /* Log technical error for any other failure reason. */
      this.setValidationError(`credentials`, strings.TECHNICAL_ERROR)
    }
  }

  renderSignUpLink() {
    const { createProfileText, hideSignUp } = this.props

    return (
      !hideSignUp && (
        <div className="p-3 wm-bg-gray">
          <ButtonArrow
            className="pl-1 has-focus"
            href={`${getLanguageRoute(this.props.language)}/user/register`}
            isExternalLink={
              (isMyWMApp && isTeamsitePage(ssrWindow.location.pathname)) ||
              !isMyWMApp
            }
            analytics_key={createProfileText}
          >
            {createProfileText}
          </ButtonArrow>
        </div>
      )
    )
  }

  renderForgotPasswordLink() {
    const { forgotEmailPwText } = this.props

    return (
      <Link
        className="d-flex p-0 d-md-block wm-link-light-green maax-font-weight-regular position-relative has-focus"
        href={`${getLanguageRoute(this.props.language)}/user/forgot-password`}
        isExternalLink
        analytics_key={forgotEmailPwText}
      >
        {forgotEmailPwText}
      </Link>
    )
  }

  renderTextInputs() {
    const {
      isAddressBarHero,
      strings,
      prefixInputID,
      emailPlaceholderText,
      passwordPlaceholderText,
      loginButtonCta,
      loginButtonAnalyticsKey,
      continueAsGuestText,
      continueAsGuestIsButton,
      handleGuestButtonClick,
      continueAsGuestAnalyticsKey,
      hideContinueAsGuest,
    } = this.props

    const isValidEmail = get(this.state, `fieldValidations.email`, false)

    const textInputs = [
      {
        "data-testid": "email-input",
        key: `email`,
        error: !!this.errorFor(`loginEmail`),
        helperText: this.errorFor(`loginEmail`),
        onChange: this.onChange(`loginEmail`, ({ target: { value } }) => {
          if (!isAddressBarHero) {
            if (value === ``) {
              this.setValidationError(`loginEmail`, `${strings.ERROR_REQUIRED}`)
            } else if (!validateLoginEmail(value)) {
              this.setValidationError(
                `loginEmail`,
                `${strings.ERROR_INVALID_EMAIL}`,
              )
            } else {
              this.setValidationError(`loginEmail`, ``)
            }
          }
          /* Clear the credential error when user changes the login email to prevent
        residual login attempt errors from persisting */
          if (this.state.errors.credentials !== ``) {
            this.setValidationError(`credentials`, ``)
          }
        }),
        onBlur: this.onBlur(`loginEmail`),
        onKeyDown: (e) => {
          if (e.key === ` `) {
            e.preventDefault()
          }

          let pastedText = ``

          if (e.clipboardData && e.clipboardData.getData) {
            pastedText = e.clipboardData.getData(`text/plain`)
          }
          pastedText = pastedText.trim()

          if (pastedText) {
            this.setState({ loginEmail: pastedText }, () => {
              this.value = pastedText
            })
          }
        },
        value: this.state.loginEmail,
        label: emailPlaceholderText,
        id: `${prefixInputID}loginEmail`,
        type: "email",
        InputProps: {
          endAdornment: isValidEmail && (
            <InputAdornment position="end">
              <img
                src={getContentUrl(
                  "/content/dam/mywm/icons/green-checkmark-icon.svg",
                )}
                alt=""
              />
            </InputAdornment>
          ),
          style: {
            paddingRight: `2rem`,
          },
        },
        style: { marginBottom: `1rem` },
      },
      {
        "data-testid": "password-input",
        error: !!this.errorFor(`loginPassword`),
        helperText: this.errorFor(`loginPassword`),
        onChange: this.onChange(`loginPassword`, ({ target: { value } }) => {
          if (value === ``) {
            this.setValidationError(
              `loginPassword`,
              `${strings.ERROR_REQUIRED}`,
            )
          } else {
            this.setValidationError(`loginPassword`, ``)
          }
          /* Clear the credential error when user changes the login password to prevent
        residual login attempt errors from persisting */
          if (this.state.errors.credentials !== ``) {
            this.setValidationError(`credentials`, ``)
          }
        }),
        onBlur: this.onBlur(`loginPassword`),
        autoComplete: `current-password`,
        label: passwordPlaceholderText,
        id: `${prefixInputID}loginPassword`,
        type: this.state.showPassword ? "text" : "password",
        InputProps: {
          endAdornment: (
            <InputAdornment position="end">
              <Button
                className="show-password-button"
                disableRipple
                style={{
                  cursor: `pointer`,
                  textDecoration: `none`,
                  fontFamily: `Maax`,
                  minWidth: `auto`,
                  color: `#1c8200`,
                }}
                onClick={() => {
                  this.setState({
                    // eslint-disable-next-line react/no-access-state-in-setstate
                    showPassword: !this.state.showPassword,
                  })
                }}
                disabled={!this.state.loginPassword}
              >
                <span className="sr-only" role="status" aria-live="polite">
                  {this.state.showPassword
                    ? `${strings.HIDE}`
                    : `${strings.SHOW}`}
                </span>
                {this.state.showPassword
                  ? `${strings.HIDE}`
                  : `${strings.SHOW}`}
              </Button>
            </InputAdornment>
          ),
        },
        style: { marginBottom: `.5rem` },
      },
    ]

    return (
      <TextInputForm
        textInputs={textInputs}
        footerChildren={
          isAddressBarHero ? (
            <div className="w-100 px-4 maax-font-weight-regular">
              <ButtonPrimary
                className="mt-1 w-100 has-focus"
                analytics_key={loginButtonAnalyticsKey || loginButtonCta}
                disabled={this.state.isSigningIn}
                onClick={this.handleLoginButtonClick}
              >
                {loginButtonCta}
              </ButtonPrimary>
              <div className="mt-4">{this.renderForgotPasswordLink()}</div>
            </div>
          ) : (
            <>
              <div className="d-flex flex-column textFormBulkModal w-100 d-md-block justify-content-center has-focus maax-font-weight-regular px-4 pb-3">
                <ButtonPrimary
                  analytics_key={loginButtonAnalyticsKey || loginButtonCta}
                  className="mt-1 w-100 has-focus"
                  disabled={this.state.isSigningIn}
                  onClick={this.handleLoginButtonClick}
                >
                  {loginButtonCta}
                </ButtonPrimary>
                <div className="d-flex mt-2 mb-4 justify-content-center">
                  {this.renderForgotPasswordLink()}
                </div>
                {!hideContinueAsGuest ? (
                  continueAsGuestIsButton && handleGuestButtonClick ? (
                    <ButtonSecondary
                      analytics_key={
                        continueAsGuestAnalyticsKey || continueAsGuestText
                      }
                      className="pl-0 my-2 w-100 has-focus"
                      onClick={handleGuestButtonClick}
                    >
                      {continueAsGuestText}
                    </ButtonSecondary>
                  ) : (
                    <Link
                      analytics_key={
                        continueAsGuestAnalyticsKey || continueAsGuestText
                      }
                      className="pl-0 my-2 has-focus maax-font-weight-regular"
                      href={`${getLanguageRoute(
                        this.props.language,
                      )}/mywm/locate`}
                      isExternalLink
                    >
                      {continueAsGuestText}
                    </Link>
                  )
                ) : null}
              </div>
              {this.renderSignUpLink()}
            </>
          )
        }
        hideNoError
      />
    )
  }

  renderValidationErrors() {
    const { strings, siteLanguage } = this.props

    if (this.state.errors.credentials !== ``) {
      return (
        <div className="mb-4">
          <div
            className={`pt-sm-2 ${
              siteLanguage.language === "fr_CA"
                ? "error-credentials_1"
                : "error-credentials"
            } maax-font-weight-regular p-3`}
            role="alert"
          >
            <button
              type="button"
              className="close-message float-right p-1"
              onClick={() => {
                this.setValidationError(`credentials`, ``)
              }}
              style={{
                background: `transparent`,
              }}
              aria-label="Close Button"
            >
              <ReactSVG
                wrapper="span"
                src={getContentUrl(
                  "/content/dam/wm/icons/close-alert-x-icon.svg",
                )}
              />
            </button>
            {this.state.errors.credentials === strings.TECHNICAL_ERROR ? (
              <div role="status" className="maax-font-weight-bold oops-msg">
                {strings.OOPS}
              </div>
            ) : null}

            {this.state.errors.credentials === strings.PASSWORD_EXPIRED_1 ? (
              <div role="status">
                {`${strings.PASSWORD_EXPIRED_1} `}
                <Link
                  isExternalLink
                  className="reset-link d-inline"
                  href={`${getLanguageRoute(
                    this.props.language,
                  )}/user/forgot-password`}
                >
                  {strings.PASSWORD_EXPIRED_2}
                </Link>
                {` ${strings.PASSWORD_EXPIRED_3}`}
              </div>
            ) : (
              this.state.errors.credentials
            )}
          </div>
        </div>
      )
    }
    return null
  }

  render() {
    const { className, isAddressBarHero, siteLanguage } = this.props

    return (
      <div data-testid="Login" aria-expanded={this.props.ariaExpanded}>
        <div
          className={`Login bg-white ${className} ${siteLanguage?.language}`}
        >
          {!isAddressBarHero && this.renderValidationErrors()}
          {this.renderTextInputs()}
        </div>
        {isAddressBarHero && (
          <div
            className="Login__address-bar-hero-error position-absolute"
            style={{
              top: 250,
            }}
          >
            {this.renderValidationErrors()}
          </div>
        )}
      </div>
    )
  }
}

// eslint-disable-next-line react-19-upgrade/no-prop-types
Login.propTypes = {
  className: PropTypes.string,
  loginButtonAnalyticsKey: PropTypes.string,
  disablePostLoginRedirect: PropTypes.bool,
  isAddressBarHero: PropTypes.bool,
  redirectUrl: PropTypes.string,
  prefixInputID: PropTypes.string,
  emailPlaceholderText: PropTypes.string,
  passwordPlaceholderText: PropTypes.string,
  loginButtonCta: PropTypes.string,
  forgotEmailPwText: PropTypes.string,
  createProfileText: PropTypes.string,
  continueAsGuestText: PropTypes.string,
  hideSignUp: PropTypes.bool,
  continueAsGuestIsButton: PropTypes.bool,
  handleGuestButtonClick: PropTypes.func,
  // Redux actions
  authenticateUser: PropTypes.func.isRequired,
  // Redux state
  siteLanguage: PropTypes.object.isRequired,
  language: PropTypes.string.isRequired,
  userAccount: PropTypes.object.isRequired,
  // From localization context
  continueAsGuestAnalyticsKey: PropTypes.string,
  hideContinueAsGuest: PropTypes.bool,
  strings: PropTypes.object.isRequired,
  ariaExpanded: PropTypes.bool,
}

// eslint-disable-next-line react-19-upgrade/no-default-props
Login.defaultProps = {
  loginButtonAnalyticsKey: ``,
  className: ``,
  continueAsGuestAnalyticsKey: ``,
  disablePostLoginRedirect: false,
  isAddressBarHero: false,
  redirectUrl: ``,
  prefixInputID: ``,
  emailPlaceholderText: ``,
  passwordPlaceholderText: ``,
  loginButtonCta: ``,
  forgotEmailPwText: ``,
  continueAsGuestText: ``,
  createProfileText: ``,
  hideSignUp: false,
  continueAsGuestIsButton: false,
  handleGuestButtonClick: null,
  hideContinueAsGuest: false,
  ariaExpanded: false,
}

export default withLocalization(strings)(withLDConsumer()(Login))
